import { useMemo, useState } from 'react';
import {
  Box, Typography, Stack, ToggleButtonGroup, ToggleButton, Paper, Chip, IconButton,
} from '@mui/material';
import { Telegram, EditOutlined, DeleteOutlineOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { SymptomReading } from '@lorrainemarwood-care/shared';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import SymptomSliders from '../components/SymptomSliders';
import SymptomChart from '../components/SymptomChart';
import SymptomEditDialog from '../components/SymptomEditDialog';

const RANGES = [
  { key: '24h', label: '24 hours', hours: 24 },
  { key: '7d', label: '7 days', hours: 24 * 7 },
  { key: '30d', label: '30 days', hours: 24 * 30 },
  { key: 'all', label: 'All', hours: null },
] as const;

export default function Symptoms() {
  const { user } = useAuth();
  const [range, setRange] = useState<typeof RANGES[number]['key']>('7d');
  const [editing, setEditing] = useState<SymptomReading | null>(null);
  const chosen = RANGES.find((r) => r.key === range)!;
  // Round to the hour so the query key is stable across renders.
  const since = useMemo(
    () => chosen.hours ? dayjs().startOf('hour').subtract(chosen.hours, 'hour').toISOString() : undefined,
    [chosen.hours],
  );

  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['symptoms', { since }],
    queryFn: () =>
      api.get<SymptomReading[]>(
        since ? `/api/symptoms?since=${encodeURIComponent(since)}` : '/api/symptoms?limit=2000',
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/symptoms/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['symptoms'] }),
  });

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>Symptoms</Typography>

      <Stack spacing={3}>
        <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
          <ToggleButtonGroup
            value={range}
            exclusive
            onChange={(_, v) => v && setRange(v)}
            size="small"
          >
            {RANGES.map((r) => (
              <ToggleButton key={r.key} value={r.key}>{r.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <SymptomChart readings={query.data ?? []} />
        <SymptomSliders />

        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>Readings</Typography>
          <Stack spacing={1.5}>
            {(query.data ?? []).map((r) => {
              const canEdit = user?.id === r.authorId || user?.role === 'admin';
              return (
                <Paper key={r.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(r.occurredAt).format('ddd DD MMM HH:mm')} · {r.authorName}
                        </Typography>
                        {r.source === 'telegram' && <Telegram fontSize="small" sx={{ color: 'text.secondary' }} />}
                      </Stack>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 1 }}>
                        {r.breathlessness != null && <Chip size="small" label={`Breath ${r.breathlessness}`} />}
                        {r.pain != null && <Chip size="small" label={`Pain ${r.pain}`} />}
                        {r.sleep != null && <Chip size="small" label={`Sleep ${r.sleep}`} />}
                        {r.appetite != null && <Chip size="small" label={`Appetite ${r.appetite}`} />}
                        {r.mood != null && <Chip size="small" label={`Mood ${r.mood}`} />}
                        {r.appearance != null && <Chip size="small" label={`Look ${r.appearance}`} />}
                      </Stack>
                      {r.notes && (
                        <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{r.notes}</Typography>
                      )}
                    </Box>
                    {canEdit && (
                      <Stack direction="row" spacing={0.25}>
                        <IconButton size="small" onClick={() => setEditing(r)}>
                          <EditOutlined fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => deleteMutation.mutate(r.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <DeleteOutlineOutlined fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      </Stack>

      <SymptomEditDialog
        reading={editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
      />
    </Box>
  );
}
